// Placement-sync divergence breadcrumb (MN-9145).
//
// Emitted by the retained post-commit safety-net effect in
// shared/ui/layouts/state.tsx when it corrects a currentPlacement that a
// writer left inconsistent with designSubTabAtom — i.e. a placement write
// that bypassed usePlacementNavigation.
//
// The `reason` discriminator separates the two write classes the safety net
// covers so the metric is not self-contaminating:
//   - "unrouted_writer": a genuine placement write that bypassed
//     usePlacementNavigation. Expected rate: zero — a non-zero rate identifies
//     an unrouted writer and gates the future removal of the effect.
//   - "init_fallback": the sanctioned useDesignSubTabInitialization raw-atom
//     write (?side= deep links, upload_urls inits, back-only project loads).
//     This is expected to carry a permanent, non-zero baseline; filter it out
//     before reading the unrouted-writer signal above.
import { track } from "./trackEvent";

/**
 * Tracks one safety-net correction of a divergent placement triple.
 *
 * Cardinality is bounded: every property is a small enum (no timestamps or
 * ids — ingestion stamps receipt time), and the event only fires on
 * divergence (never on routine navigation).
 *
 * @param options - tab, active sub-tab, the stale/corrected placements, and
 *   the `reason` discriminator distinguishing a genuine unrouted writer from
 *   the sanctioned init fallback
 */
export const trackPlacementSyncDivergence = (options: {
  tab: string;
  activeSubTab: "front" | "back";
  fromPlacement: string;
  toPlacement: string;
  reason: "unrouted_writer" | "init_fallback";
}) => {
  track("design_lab_placement_sync_divergence", {
    tab: options.tab,
    active_sub_tab: options.activeSubTab,
    from_placement: options.fromPlacement,
    to_placement: options.toPlacement,
    reason: options.reason
  });
};
